import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGame } from './GameContext';
import './GameLobby.css';

function useCountdown(brokeAt, waitDurationMs) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!brokeAt) return;
    const tick = () => {
      const ms = Math.max(0, brokeAt + waitDurationMs - Date.now());
      setRemaining(prev => {
        if (ms === 0 && prev > 0) window.location.reload();
        return ms;
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [brokeAt, waitDurationMs]);

  return remaining ?? 0;
}

function formatCountdown(ms) {
  const totalSeconds = Math.ceil(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function GameLobby({ user }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const upper = (str) => str.toLocaleUpperCase(i18n.language);
  const { balance, completedCases, stats, wrongAttempts, unlockedLevel, levels, totalCaseCount,
          brokeAt, waitDurationMs, canPlay,
          gameDataLoading, gameDataError } = useGame();
  const remaining = useCountdown(brokeAt, waitDurationMs);
  const currentLevelRef = useRef(null);

  useEffect(() => {
    if (currentLevelRef.current) {
      currentLevelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [gameDataLoading]);

  const formatMoney = (n) =>
    n < 0 ? `-${Math.abs(n).toLocaleString()}` : `${n.toLocaleString()}`;

  if (gameDataLoading) {
    return (
      <div className="lobby-container">
        <header className="lobby-header">
          <div className="lobby-brand">
            <img src="/favicon.ico" alt="Logo" className="lobby-logo" style={{ width: '24px', height: '24px' }} />
            <span className="lobby-title">CostlyCure</span>
          </div>
          <div className="lobby-header-right">
            <div className="skeleton skeleton-wallet" />
            <div className="skeleton skeleton-btn" />
            <div className="skeleton skeleton-user" />
          </div>
        </header>
        <div className="lobby-body">
          <div className="stats-bar">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="stat-item">
                <div className="skeleton skeleton-stat-value" />
                <div className="skeleton skeleton-stat-label" />
              </div>
            ))}
          </div>
          <div className="lobby-instructions">
            <div className="skeleton skeleton-heading" />
            <div className="skeleton skeleton-subtext" />
          </div>
          <div className="level-list">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="level-row skeleton-row">
                <div className="skeleton skeleton-badge-circle" />
                <div className="level-info">
                  <div className="skeleton skeleton-name" />
                  <div className="skeleton skeleton-complaint" />
                </div>
                <div className="skeleton skeleton-btn" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (gameDataError) {
    return (
      <div className="lobby-container lobby-centered">
        <div className="lobby-error-card">
          <span className="lobby-error-icon">⚠️</span>
          <p className="lobby-error-title">{t('lobby.failedToLoad')}</p>
          <p className="lobby-error-detail">{gameDataError}</p>
        </div>
      </div>
    );
  }

  if (!canPlay) {
    return (
      <div className="lobby-container">
        <header className="lobby-header">
          <div className="lobby-brand">
            <img src="/favicon.ico" alt="Logo" className="lobby-logo" style={{ width: '24px', height: '24px' }} />
            <span className="lobby-title">CostlyCure</span>
          </div>
          <div className="lobby-header-right">
            <button className="settings-btn" onClick={() => navigate('/game/settings')} title="Settings">
              ⚙️ {t('lobby.settings')}
            </button>
          </div>
        </header>
        <div className="lobby-wait-screen">
          <div className="wait-icon">💸</div>
          <h2 className="wait-title">{t('lobby.outOfFunds')}</h2>
          <p className="wait-desc">{t('lobby.outOfFundsDesc')}</p>
          <div className="wait-timer">
            <span className="wait-timer-label">{t('lobby.timeRemaining')}</span>
            <span className="wait-timer-value">{formatCountdown(remaining)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lobby-container">
      {/* Header */}
      <header className="lobby-header">
        <div className="lobby-brand">
          <img src="/favicon.ico" alt="Logo" className="lobby-logo" style={{ width: '24px', height: '24px' }} />
          <span className="lobby-title">CostlyCure</span>
        </div>
        <div className="lobby-header-right">
          <div className="wallet-badge">
            <span className="wallet-icon">💰</span>
            <span className={`wallet-amount ${balance < 0 ? 'negative' : ''}`}>
              {formatMoney(balance)}
            </span>
          </div>
          <button
            className="settings-btn"
            onClick={() => navigate('/game/settings')}
            title="Settings"
          >
            ⚙️ {t('lobby.settings')}
          </button>
          <div className="user-menu">
            <span className="user-name">{user?.name || user?.email || t('lobby.doctor')}</span>
          </div>
        </div>
      </header>

      <div className="lobby-body">
        {/* Stats bar */}
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value correct">{stats.correct}</span>
            <span className="stat-label">{upper(t('lobby.correctDiagnoses'))}</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-value wrong">{wrongAttempts}</span>
            <span className="stat-label">{upper(t('lobby.wrongAttempts'))}</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-value earned">{formatMoney(stats.totalEarned)}</span>
            <span className="stat-label">{upper(t('lobby.totalEarned'))}</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-value">{Object.keys(completedCases).length}/{totalCaseCount || levels.length}</span>
            <span className="stat-label">{upper(t('lobby.levelsCleared'))}</span>
          </div>
        </div>

        {/* Level map */}
        <div className="lobby-instructions">
          <h2>{t('lobby.levelSelect')}</h2>
          <p>
            {t('lobby.levelSelectDesc')}
          </p>
        </div>

        <div className="level-list">
          {levels.map((patient) => {
            const isCompleted = !!completedCases[patient.id]?.correct;
            const isUnlocked = patient.level <= unlockedLevel;
            const isCurrent = patient.level === unlockedLevel;
            const isMale = patient.gender === 'Male' || patient.gender === 'Erkek';

            return (
              <div
                key={patient.id}
                ref={isCurrent ? currentLevelRef : null}
                className={`level-row ${isCompleted ? 'level-done' : isCurrent ? 'level-current' : isUnlocked ? 'level-unlocked' : 'level-locked'}`}
                onClick={() => isCompleted ? navigate(`/game/patient/${patient.id}?review=true`) : isUnlocked && navigate(`/game/patient/${patient.id}`)}
              >
                <div className="level-num-badge">
                  {isCompleted ? '✓' : isUnlocked ? patient.level : '🔒'}
                </div>

                <div className="level-info">
                  {isUnlocked ? (
                    <>
                      <div className="level-patient-name">
                        {isMale ? '👨' : '👩'} {patient.name}
                        <span className="level-age-gender">{patient.age}y · {patient.gender}</span>
                      </div>
                      <div className="level-complaint">&ldquo;{patient.chiefComplaint}&rdquo;</div>
                    </>
                  ) : (
                    <>
                      <div className="level-patient-name level-mystery">??? · {t('lobby.ageUnknown')}</div>
                      <div className="level-complaint level-mystery">
                        {t('lobby.completeLevelToUnlock', { level: patient.level - 1 })}
                      </div>
                    </>
                  )}
                </div>

                <div className="level-action">
                  {isCompleted ? (
                    <div className="level-completed-actions">
                      <div className="level-earned">
                        💰 +{formatMoney(completedCases[patient.id].earned)}
                      </div>
                      <button
                        className="level-play-btn"
                        onClick={(e) => { e.stopPropagation(); navigate(`/game/patient/${patient.id}?review=true`); }}
                      >
                        {t('lobby.review')}
                      </button>
                    </div>
                  ) : isUnlocked ? (
                    <button
                      className={`level-play-btn ${isCurrent ? 'level-play-btn-current' : ''}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/game/patient/${patient.id}`); }}
                    >
                      {isCurrent ? t('lobby.play') : t('lobby.retry')}
                    </button>
                  ) : (
                    <div className="level-locked-label">{t('lobby.locked')}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
